/**
 * Project Control. The persistent strip answers the captain question at all
 * times; the now/next card answers what is moving, what is next, who enables it
 * and what could stop it.
 */
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { DecisionRow } from '@dcc/shared';
import { api, ApiRequestError, idempotencyKey } from '../lib/api';
import { useProjection } from '../hooks/useProjection';
import { useLive } from '../hooks/useLive';
import {
  CapacityLayer,
  Confidence,
  FreshnessTag,
  Panel,
  Stat,
  StateChip,
  ToneChip,
  Unknown,
} from '../components/Primitives';
import {
  DegradedBanner,
  EmptyState,
  ErrorState,
  LiveBadge,
  LowConfidenceNote,
  NoDataState,
  SkeletonPanel,
} from '../components/States';
import { JourneyCanvas } from '../components/JourneyCanvas';
import { JourneyList } from '../components/JourneyList';
import { GovernorPanel } from '../components/GovernorPanel';
import { absoluteTime, duration, relativeTime } from '../lib/format';
import { toneFor } from '../lib/tone';
import { useIsPhone } from '../hooks/useMediaQuery';

export function ProjectControl() {
  const { projectId = '' } = useParams();
  const isPhone = useIsPhone();
  const projection = useProjection(() => api.project(projectId), [projectId]);
  const live = useLive(
    ['project.updated', 'journey.updated', 'lease.updated', 'incident.updated', 'capacity.updated'],
    projection.reload,
  );

  if (projection.loading) return <SkeletonPanel rows={6} />;
  if (projection.error) return <ErrorState error={projection.error} onRetry={projection.reload} />;
  const view = projection.data!;
  const captain = view.captain;

  return (
    <>
      {/* Sticky on a phone: health, captain and the next action stay reachable. */}
      <div className="stickyhead">
        <div className="spread">
          <strong>{view.project.name}</strong>
          <StateChip kind="project" state={view.project.status} />
        </div>
        <p className="tiny dim" style={{ marginTop: 2 }}>
          {captain
            ? `captain ${captain.ownerName ?? captain.ownerSessionId ?? 'unknown'} · epoch ${captain.epoch} · ${captain.state.toLowerCase()}`
            : 'no captain'}
        </p>
      </div>

      <header className="topstrip">
        <div>
          <div className="label">
            {view.project.slug} · {view.version?.key ?? 'no version'}
          </div>
          <h1 className="topstrip__title">{view.project.name}</h1>
          <p className="small dim">{view.project.statusReason}</p>
        </div>
        <div className="topstrip__meta">
          <StateChip kind="project" state={view.project.status} size="lg" />
          <LiveBadge connection={live.connection} lastEventAt={live.lastEventAt} />
          <Link className="btn btn--sm" to={`/projects/${projectId}/journey`}>
            Open Journey
          </Link>
        </div>
      </header>

      {projection.degraded && (
        <DegradedBanner onRetry={projection.reload} loadedAt={projection.loadedAt} />
      )}

      {/* Permanent captain / lease strip. */}
      <Panel
        title="Orchestrator"
        subtitle="Exactly one captain per workstream. Every privileged write is fenced by the epoch."
        tone={captain ? toneFor('lease', captain.state) : 'neutral'}
        actions={<FreshnessTag freshness={view.freshness} prefix="source" />}
      >
        {view.leases.length === 0 ? (
          <EmptyState
            title="No orchestrator scope declared"
            body="This project has no lease scope yet, so nothing can be dispatched. Declare the orchestrator scope in the manifest."
          />
        ) : (
          <div className="grid grid--tiles">
            {view.leases.map((lease) => (
              <div
                key={lease.workstream}
                className="card card--toned"
                data-tone={toneFor('lease', lease.state)}
              >
                <div className="spread">
                  <strong>{lease.workstream}</strong>
                  <StateChip kind="lease" state={lease.state} />
                </div>
                <dl
                  className="small"
                  style={{
                    margin: '10px 0 0',
                    display: 'grid',
                    gridTemplateColumns: '1fr auto',
                    gap: 4,
                  }}
                >
                  <dt className="dim">Captain</dt>
                  <dd className="mono" style={{ margin: 0 }}>
                    {lease.ownerName ?? lease.ownerSessionId ?? '—'}
                  </dd>
                  <dt className="dim">Epoch (fencing token)</dt>
                  <dd className="mono" style={{ margin: 0 }}>
                    {lease.epoch}
                  </dd>
                  <dt className="dim">Highest accepted</dt>
                  <dd className="mono" style={{ margin: 0 }}>
                    {lease.highestAcceptedEpoch}
                  </dd>
                  <dt className="dim">Lease expires</dt>
                  <dd className="mono" style={{ margin: 0 }}>
                    {lease.secondsRemaining === null
                      ? '—'
                      : lease.secondsRemaining > 0
                        ? `in ${lease.secondsRemaining}s`
                        : 'expired'}
                  </dd>
                  <dt className="dim">Heartbeat</dt>
                  <dd className="mono" style={{ margin: 0 }}>
                    {relativeTime(lease.lastHeartbeatAt)} (every {lease.renewEverySeconds}s)
                  </dd>
                  <dt className="dim">Standby</dt>
                  <dd className="mono" style={{ margin: 0 }}>
                    {lease.standbyName ?? 'none'}
                  </dd>
                </dl>
                {lease.state === 'AT_RISK' && (
                  <p
                    className="tiny"
                    data-tone="risk"
                    style={{ color: 'var(--risk)', marginTop: 8 }}
                  >
                    Renewal is overdue. Dispatch pauses now, before expiry — a standby still cannot
                    write until the store confirms the lease has ended.
                  </p>
                )}
                {lease.state === 'SPLIT_BRAIN_DETECTED' && (
                  <p
                    className="tiny"
                    data-tone="critical"
                    style={{ color: 'var(--critical)', marginTop: 8 }}
                  >
                    Two owners claimed this scope. All dispatch is frozen until an operator confirms
                    which session is really running and clears the incident.
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </Panel>

      {/* Now / next / what could stop it. */}
      <div className="grid grid--sidebar">
        <Panel
          title="Now and next"
          subtitle="What is moving, what comes next, and what could stop it."
        >
          <div className="stack">
            <div>
              <div className="label">Moving now</div>
              <p>{view.nowNext.movingNow}</p>
              <p className="small dim">{view.nowNext.movingNowDetail}</p>
            </div>
            <div className="divider" />
            <div>
              <div className="label">Expected next</div>
              <p>{view.nowNext.expectedNext}</p>
              <p className="small dim">{view.nowNext.expectedNextDetail}</p>
            </div>
            <div className="divider" />
            <div className="grid grid--2">
              <div>
                <div className="label">Enabled by</div>
                <p className="small">{view.nowNext.enabledBy}</p>
              </div>
              <div>
                <div className="label">Could stop it</div>
                <p className="small">{view.nowNext.couldStopIt}</p>
              </div>
            </div>
            {view.nowNext.interventionNeededAt && (
              <p className="small" data-tone="risk" style={{ color: 'var(--risk)' }}>
                A person is needed by {absoluteTime(view.nowNext.interventionNeededAt)} (
                {relativeTime(view.nowNext.interventionNeededAt)}).
              </p>
            )}
          </div>
        </Panel>

        <Panel title="Finish forecast" subtitle="Derived, and reproducible from the inputs below.">
          <div className="stack">
            <Stat
              label="Estimated finish"
              large
              value={
                view.forecast.etaAt ? (
                  relativeTime(view.forecast.etaAt)
                ) : (
                  <Unknown why={view.forecast.reasons.join(' ')} />
                )
              }
              note={view.forecast.etaAt ? absoluteTime(view.forecast.etaAt) : undefined}
            />
            <Confidence
              confidence={view.forecast.confidence}
              percentValue={view.forecast.confidencePercent}
              reasons={view.forecast.reasons}
            />
            {view.forecast.deltaVsOriginalHours !== null && (
              <p
                className="small"
                data-tone={view.forecast.deltaVsOriginalHours > 0 ? 'risk' : 'success'}
                style={{ color: 'var(--tone)' }}
              >
                {view.forecast.deltaVsOriginalHours > 0
                  ? `${duration(view.forecast.deltaVsOriginalHours)} later than the original commitment`
                  : `${duration(Math.abs(view.forecast.deltaVsOriginalHours))} earlier than the original commitment`}
                {view.originalTargetAt && (
                  <span className="dim"> (target {absoluteTime(view.originalTargetAt)})</span>
                )}
              </p>
            )}
            {view.forecast.drivers.length > 0 && (
              <div>
                <div className="label">What makes up the estimate</div>
                <ul
                  className="small stack stack--tight"
                  style={{ margin: '6px 0 0', paddingLeft: 16 }}
                >
                  {view.forecast.drivers.map((driver) => (
                    <li key={driver.label}>
                      <strong>{driver.label}</strong> — {duration(driver.hours)}
                      <div className="tiny dim">{driver.detail}</div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <LowConfidenceNote reasons={view.forecast.reasons} />
            <p className="tiny dim mono">calculation {view.forecast.calculationVersion}</p>
          </div>
        </Panel>
      </div>

      {/* Capacity cockpit: speed, session range, weekly fuel, team range. */}
      <Panel
        title="Capacity"
        subtitle="Speed is accepted output. Range, fuel and team range are separate layers and may disagree."
      >
        <div className="grid grid--4">
          <div className="stack stack--tight">
            <span className="label">Speed</span>
            <span className="stat__value">
              {view.capacity.speed.acceptedSlicesPerDay ?? 0}
              <span className="stat__unit"> slices/day</span>
            </span>
            <span className="tiny dim">
              {view.capacity.speed.journeysPerDay ?? 0} journeys/day · {view.capacity.speed.detail}
            </span>
          </div>
          <CapacityLayer layer={view.capacity.sessionRange} title="Session range" />
          <CapacityLayer layer={view.capacity.weeklyFuel} title="Weekly fuel" />
          <div className="stack stack--tight">
            <div className="spread">
              <span className="label">Team range</span>
              <StateChip kind="capacity" state={view.capacity.teamRange.state} />
            </div>
            <span className="stat__value">
              {view.capacity.teamRange.totalAgentHours === null ? (
                <Unknown />
              ) : (
                <>
                  {view.capacity.teamRange.totalAgentHours}
                  <span className="stat__unit"> agent-h</span>
                </>
              )}
            </span>
            <span className="tiny dim">
              {view.capacity.teamRange.requiredAgentHours === null
                ? 'no workload estimate'
                : `${duration(view.capacity.teamRange.requiredAgentHours)} of work remaining`}
            </span>
            <span className="tiny dim">
              Eligible:{' '}
              {view.capacity.teamRange.eligibleMembers.map((m) => m.name).join(', ') || 'nobody'}
            </span>
          </div>
        </div>
        {view.capacity.teamRange.excluded.length > 0 && (
          <>
            <div className="divider" />
            <div className="label">Not counted in team range</div>
            <ul className="small stack stack--tight" style={{ margin: '6px 0 0', paddingLeft: 16 }}>
              {view.capacity.teamRange.excluded.map((entry) => (
                <li key={entry.memberId}>
                  <strong>{entry.name}</strong> — {entry.reason}
                </li>
              ))}
            </ul>
          </>
        )}
      </Panel>

      <GovernorPanel governor={view.governor} />

      {/* Journey excerpt with the next testable station. */}
      <Panel
        title="Journey excerpt"
        subtitle="Where the project is on its route, and what becomes testable next."
        actions={
          <Link className="btn btn--sm btn--quiet" to={`/projects/${projectId}/journey`}>
            Full journey
          </Link>
        }
        flush
      >
        {/* One or the other is rendered, never both: a hidden copy of the
            journey would be duplicate content for a screen reader. */}
        {isPhone ? (
          <div style={{ padding: 16 }}>
            <JourneyList
              journey={view.journeyExcerpt}
              selectedId={view.nextStation?.id ?? null}
              onSelect={() => undefined}
              routeFilter={null}
            />
          </div>
        ) : (
          <JourneyCanvas
            journey={view.journeyExcerpt}
            selectedId={view.nextStation?.id ?? null}
            onSelect={() => undefined}
            routeFilter={null}
            focusCriticalPath={false}
          />
        )}
        {view.nextStation && (
          <div style={{ padding: 16, borderTop: '1px solid var(--border)' }}>
            <div className="label">Next testable station</div>
            <div className="spread" style={{ marginTop: 4 }}>
              <strong>{view.nextStation.title}</strong>
              <StateChip kind="workItem" state={view.nextStation.status} />
            </div>
            <p className="small dim">
              {view.nextStation.station?.whatIsTestable ?? 'No station payload recorded yet.'}
            </p>
            <Link className="btn btn--sm" to={`/projects/${projectId}/journey`}>
              Review the station
            </Link>
          </div>
        )}
      </Panel>

      <WorkstreamsPanel view={view} />
      <DecisionsPanel view={view} projectId={projectId} onChanged={projection.reload} />
      <RepositoryActivityPanel view={view} />
      <DeliveryPanel view={view} />

      <Panel title="Activity" subtitle="Events for this project, newest first.">
        {view.activity.length === 0 ? (
          <NoDataState what="activity" connected={view.connections.length > 0} />
        ) : (
          <ul className="stack stack--tight" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {view.activity.slice(0, 20).map((item) => (
              <li key={item.id} className="spread small" data-tone={item.tone}>
                <span className="truncate">{item.summary}</span>
                <span className="tiny dim mono" style={{ whiteSpace: 'nowrap' }}>
                  {item.correlationId ?? ''} · {relativeTime(item.at)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </>
  );
}

function WorkstreamsPanel({
  view,
}: {
  view: NonNullable<ReturnType<typeof api.project> extends Promise<infer T> ? T : never>;
}) {
  return (
    <Panel
      title="Parallel workstreams"
      subtitle="Who and what is driving each route, and what each one is waiting on."
      flush
    >
      <div className="rows">
        <div
          className="row row--head desktop-only"
          style={{ gridTemplateColumns: '1.4fr 1fr 1.1fr 1fr 0.8fr 0.9fr' }}
        >
          <span>Slice</span>
          <span>Status</span>
          <span>Driver</span>
          <span>Repo / branch</span>
          <span>Depends on</span>
          <span>ETA</span>
        </div>
        {view.workstreams.length === 0 ? (
          <div style={{ padding: 16 }}>
            <EmptyState
              tone="success"
              title="Nothing open"
              body="Every slice in this version is done, skipped or cancelled."
            />
          </div>
        ) : (
          view.workstreams.map((row) => (
            <div
              key={row.id}
              className="row"
              style={{ gridTemplateColumns: '1.4fr 1fr 1.1fr 1fr 0.8fr 0.9fr' }}
              data-tone={toneFor('workItem', row.status)}
            >
              <div className="row__stack row__cell" data-label="Slice">
                <strong className="truncate">
                  {row.workItemKey} {row.workItemTitle}
                </strong>
                <span className="row__sub">{row.workstream}</span>
                {row.warnings.map((warning) => (
                  <span
                    key={warning}
                    className="row__sub"
                    data-tone="risk"
                    style={{ color: 'var(--risk)' }}
                  >
                    {warning}
                  </span>
                ))}
              </div>
              <div className="row__stack row__cell" data-label="Status">
                <StateChip kind="workItem" state={row.status} />
                <span className="row__sub">{row.statusCause}</span>
              </div>
              <div className="row__stack row__cell" data-label="Driver">
                <span className="truncate">{row.driverLabel}</span>
                <span className="row__sub mono">{row.sessionId ?? 'no session'}</span>
              </div>
              <div className="row__stack row__cell" data-label="Repository and branch">
                <span className="truncate mono">{row.service ?? row.repository ?? '—'}</span>
                <span className="row__sub mono truncate">{row.branch ?? '—'}</span>
                {row.pr && <span className="row__sub mono truncate">{row.pr}</span>}
              </div>
              <div className="row__cell" data-label="Depends on">
                <span className="small">
                  {row.dependsOn.length === 0 ? 'nothing' : row.dependsOn.join(', ')}
                </span>
              </div>
              <div className="row__stack row__cell" data-label="ETA">
                <span className="small">{row.etaAt ? relativeTime(row.etaAt) : '—'}</span>
                <span className="row__sub">
                  {row.burnPercent === null ? 'burn unknown' : `${row.burnPercent}% burned`}
                </span>
              </div>
            </div>
          ))
        )}
      </div>
    </Panel>
  );
}

function DecisionsPanel({
  view,
  projectId,
  onChanged,
}: {
  view: Awaited<ReturnType<typeof api.project>>;
  projectId: string;
  onChanged: () => void;
}) {
  const open = view.decisions.filter((d) => d.status === 'OPEN');
  const blockers = view.blockers.filter((b) => b.status === 'OPEN');

  return (
    <div className="grid grid--2">
      <Panel
        title="Decisions"
        subtitle="Directly answerable here; the answer and its reason are audited."
      >
        {open.length === 0 ? (
          <EmptyState
            tone="success"
            title="No open decision"
            body="Nothing is waiting on a human decision."
          />
        ) : (
          <div className="stack">
            {open.map((decision) => (
              <DecisionCard
                key={decision.id}
                decision={decision}
                projectId={projectId}
                onChanged={onChanged}
              />
            ))}
          </div>
        )}
      </Panel>

      <Panel
        title="Blockers"
        subtitle="Confirmed stops with cause, impact and the recommended action."
      >
        {blockers.length === 0 ? (
          <EmptyState
            tone="success"
            title="No open blocker"
            body="Nothing is confirmed as blocked."
          />
        ) : (
          <div className="stack">
            {blockers.map((blocker) => (
              <article
                key={blocker.id}
                className="card card--toned"
                data-tone={toneFor('severity', blocker.severity)}
              >
                <div className="spread">
                  <strong>{blocker.title}</strong>
                  <StateChip kind="severity" state={blocker.severity} />
                </div>
                <p className="small" style={{ marginTop: 6 }}>
                  {blocker.cause}
                </p>
                <p className="small dim">{blocker.impact}</p>
                <p className="small">
                  <strong>Next:</strong> {blocker.recommendedAction}
                </p>
                <p className="tiny dim">
                  owner {blocker.ownerName ?? 'unassigned'} · open for{' '}
                  {Math.round(blocker.ageMinutes / 60)}h ·{' '}
                  {blocker.onCriticalPath ? 'on the critical path' : 'off the critical path'}
                </p>
              </article>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}

function DecisionCard({
  decision,
  projectId,
  onChanged,
}: {
  decision: DecisionRow;
  projectId: string;
  onChanged: () => void;
}) {
  const [option, setOption] = useState(decision.options[0]?.key ?? '');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const overdue = decision.requiredBy !== null && Date.parse(decision.requiredBy) < Date.now();

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await api.resolveDecision(projectId, decision.id, option, reason, idempotencyKey('decision'));
      onChanged();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'That could not be recorded.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <article
      className="card card--toned"
      data-tone={overdue ? 'risk' : toneFor('severity', decision.severity)}
    >
      <div className="spread">
        <strong>{decision.question}</strong>
        <StateChip kind="severity" state={decision.severity} />
      </div>
      <p className="small" style={{ marginTop: 6 }}>
        {decision.context}
      </p>
      <p className="small dim">{decision.impact}</p>
      <p className="tiny dim">
        owner {decision.ownerName ?? 'unassigned'} · open {Math.round(decision.ageMinutes / 60)}h ·{' '}
        {decision.requiredBy
          ? `${overdue ? 'overdue since' : 'due'} ${relativeTime(decision.requiredBy)}`
          : 'no deadline set'}
      </p>

      <div className="stack stack--tight" style={{ marginTop: 10 }}>
        {decision.options.map((choice) => (
          <label
            key={choice.key}
            className="inline inline--tight small"
            style={{ alignItems: 'flex-start' }}
          >
            <input
              type="radio"
              name={`decision-${decision.id}`}
              value={choice.key}
              checked={option === choice.key}
              onChange={() => setOption(choice.key)}
              style={{ marginTop: 4 }}
            />
            <span>
              <strong>{choice.label}</strong>
              <span className="tiny dim" style={{ display: 'block' }}>
                {choice.consequence}
              </span>
            </span>
          </label>
        ))}
        <label className="stack stack--tight">
          <span className="label">Reason</span>
          <textarea
            className="btn"
            style={{ minHeight: 56, padding: 8, justifyContent: 'flex-start', textAlign: 'left' }}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </label>
        <button
          type="button"
          className="btn btn--primary btn--sm"
          disabled={busy || reason.trim().length < 3 || !option}
          onClick={() => void submit()}
        >
          Record the decision
        </button>
        {error && (
          <p className="tiny" data-tone="risk" style={{ color: 'var(--risk)' }} role="alert">
            {error}
          </p>
        )}
      </div>
    </article>
  );
}

function DeliveryPanel({ view }: { view: Awaited<ReturnType<typeof api.project>> }) {
  return (
    <div className="grid grid--2">
      <Panel
        title="Builds and tests"
        subtitle="Latest per service, with artifact and commit provenance."
        flush
      >
        <div className="rows">
          {view.builds.length === 0 ? (
            <div style={{ padding: 16 }}>
              <NoDataState what="builds" connected={view.connections.length > 0} />
            </div>
          ) : (
            view.builds.slice(0, 6).map((build) => (
              <div
                key={build.id}
                className="row"
                style={{ gridTemplateColumns: '1fr auto' }}
                data-tone={toneFor('build', build.status)}
              >
                <div className="row__stack row__cell" data-label="Build">
                  <span className="inline inline--tight">
                    <StateChip kind="build" state={build.status} />
                    <strong>{build.serviceName ?? 'unknown service'}</strong>
                  </span>
                  <span className="row__sub mono truncate">
                    {build.branch} · {build.sha} · {build.artifactRef ?? 'no artifact'}
                  </span>
                  <span className="row__sub">
                    {build.testsPassed ?? '?'} passed
                    {build.testsFailed ? `, ${build.testsFailed} failed` : ''}
                  </span>
                </div>
                <div className="row__cell" data-label="Observed">
                  <FreshnessTag freshness={build.freshness} />
                </div>
              </div>
            ))
          )}
        </div>
      </Panel>

      <Panel
        title="Deployments"
        subtitle="Environment, artifact provenance and rollback state."
        flush
      >
        <div className="rows">
          {view.deployments.length === 0 ? (
            <div style={{ padding: 16 }}>
              <NoDataState what="deployments" connected={view.connections.length > 0} />
            </div>
          ) : (
            view.deployments.slice(0, 6).map((deployment) => (
              <div
                key={deployment.id}
                className="row"
                style={{ gridTemplateColumns: '1fr auto' }}
                data-tone={toneFor('deployment', deployment.status)}
              >
                <div className="row__stack row__cell" data-label="Deployment">
                  <span className="inline inline--tight">
                    <StateChip kind="deployment" state={deployment.status} />
                    <strong>
                      {deployment.environmentName} · {deployment.serviceName ?? 'unknown service'}
                    </strong>
                  </span>
                  <span className="row__sub mono truncate">
                    {deployment.sha}
                    {deployment.urlRef ? ` · ${deployment.urlRef}` : ''}
                  </span>
                  {deployment.externallyOriginated && (
                    <span
                      className="row__sub"
                      data-tone="attention"
                      style={{ color: 'var(--attention)' }}
                    >
                      No build in DCC matches this artifact; it is recorded as externally
                      originated.
                    </span>
                  )}
                </div>
                <div
                  className="row__stack row__cell"
                  data-label="Observed"
                  style={{ alignItems: 'flex-end' }}
                >
                  <FreshnessTag freshness={deployment.freshness} />
                  {deployment.rollbackAvailable && (
                    <span className="tiny dim">rollback available</span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </Panel>
    </div>
  );
}

/**
 * What the provider says is in the code right now.
 *
 * This sits beside builds and deployments rather than replacing them: a green
 * build says the pipeline agreed, and an open pull request says a person is
 * still waiting on someone. They are different facts and both are operational.
 */
function RepositoryActivityPanel({ view }: { view: Awaited<ReturnType<typeof api.project>> }) {
  const activity = view.repositoryActivity;
  const connected = view.connections.some((c) => c.state === 'CONNECTED');

  if (activity.neverObserved) {
    return (
      <Panel title="Repository activity" subtitle="Read directly from the provider.">
        <NoDataState what="repository activity" connected={connected} />
      </Panel>
    );
  }

  const openPulls = activity.pullRequests.filter((pr) => pr.state === 'OPEN');
  const settledPulls = activity.pullRequests.filter((pr) => pr.state !== 'OPEN');

  return (
    <div className="grid grid--2">
      <Panel
        title={`Pull requests (${openPulls.length} open)`}
        subtitle="Open first, then the most recently settled."
        flush
      >
        <div className="rows">
          {activity.pullRequests.length === 0 ? (
            <div style={{ padding: 16 }}>
              <NoDataState what="pull requests" connected={connected} />
            </div>
          ) : (
            [...openPulls, ...settledPulls].slice(0, 6).map((pr) => (
              <div
                key={pr.id}
                className="row"
                style={{ gridTemplateColumns: '1fr auto' }}
                data-tone={pr.state === 'OPEN' ? (pr.draft ? 'neutral' : 'active') : 'success'}
              >
                <div className="row__stack row__cell" data-label="Pull request">
                  <span className="inline inline--tight">
                    <ToneChip
                      tone={pr.state === 'OPEN' ? (pr.draft ? 'neutral' : 'active') : 'success'}
                    >
                      {pr.state === 'OPEN' && pr.draft ? 'DRAFT' : pr.state}
                    </ToneChip>
                    <strong className="truncate">
                      #{pr.number} {pr.title}
                    </strong>
                  </span>
                  <span className="row__sub mono truncate">
                    {pr.headBranch} → {pr.baseBranch} · {pr.repository}
                  </span>
                  <span className="row__sub">
                    {pr.mergedAt
                      ? `merged by ${pr.mergedByLogin ?? 'someone'} ${relativeTime(pr.mergedAt)}`
                      : `opened by ${pr.authorLogin ?? 'someone'} ${relativeTime(pr.openedAt)}`}
                  </span>
                </div>
                <div className="row__cell" data-label="Observed">
                  <FreshnessTag freshness={pr.freshness} />
                </div>
              </div>
            ))
          )}
        </div>
      </Panel>

      <Panel title="Commits and branches" subtitle="Newest first." flush>
        <div className="rows">
          {activity.branches.slice(0, 3).map((branch) => (
            <div
              key={`${branch.repository}:${branch.name}`}
              className="row"
              style={{ gridTemplateColumns: '1fr auto' }}
            >
              <div className="row__stack row__cell" data-label="Branch">
                <span className="inline inline--tight">
                  <ToneChip tone="neutral">{branch.isDefault ? 'DEFAULT' : 'BRANCH'}</ToneChip>
                  <strong className="mono truncate">{branch.name}</strong>
                </span>
                <span className="row__sub mono truncate">
                  {branch.headSha.slice(0, 7)} · {branch.repository}
                  {branch.protected ? ' · protected' : ' · unprotected'}
                </span>
              </div>
              <div className="row__cell" data-label="Observed">
                <FreshnessTag freshness={branch.freshness} />
              </div>
            </div>
          ))}
          {activity.commits.slice(0, 6).map((commit) => (
            <div key={commit.sha} className="row" style={{ gridTemplateColumns: '1fr auto' }}>
              <div className="row__stack row__cell" data-label="Commit">
                <span className="truncate">{commit.message}</span>
                <span className="row__sub mono truncate">
                  {commit.shortSha} · {commit.authorLogin ?? commit.authorName ?? 'unknown author'}
                  {commit.branch ? ` · ${commit.branch}` : ''}
                </span>
              </div>
              <div className="row__cell" data-label="Committed">
                <span className="tiny dim">{relativeTime(commit.committedAt)}</span>
              </div>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}
